import {
  type Address,
  type ProgramDerivedAddress,
  getAddressCodec,
  getProgramDerivedAddress,
} from '@solana/kit';
import {
  CPMM_PROGRAM_ID,
  SEED_CONFIG,
  SEED_POOL,
  SEED_SPOT_POOL,
  SEED_AUTHORITY,
  SEED_VAULT0,
  SEED_VAULT1,
  SEED_POSITION,
  SEED_ORACLE,
  SEED_PROTOCOL_FEE_OWNER,
  HF_BEFORE_SWAP,
  HF_AFTER_SWAP,
  SPOT_HOOK_FLAG_MASK,
  SYSTEM_PROGRAM_ADDRESS,
} from './constants.js';

const addressCodec = getAddressCodec();
const textEncoder = new TextEncoder();

function u16Seed(value: number): Uint8Array {
  if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
    throw new RangeError('u16 value must be between 0 and 65535');
  }

  const bytes = new Uint8Array(2);
  new DataView(bytes.buffer).setUint16(0, value, true);
  return bytes;
}

function u32Seed(value: number): Uint8Array {
  if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
    throw new RangeError('u32 value must be between 0 and 4294967295');
  }

  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value, true);
  return bytes;
}

function assertHookedSpotPoolConfig(
  hookProgram: Address,
  hookFlags: number,
): void {
  if (hookProgram === SYSTEM_PROGRAM_ADDRESS) {
    throw new Error('hookProgram must identify a non-default program');
  }

  u32Seed(hookFlags);
  if (
    hookFlags !== HF_BEFORE_SWAP &&
    hookFlags !== HF_AFTER_SWAP &&
    hookFlags !== SPOT_HOOK_FLAG_MASK
  ) {
    throw new Error(
      'hookFlags must enable before-swap, after-swap, or both for a spot pool',
    );
  }
}

// ============================================================================
// Token Sorting
// ============================================================================

/**
 * Sort two mints into canonical order (token0 < token1 by bytes)
 * This is required for Pool PDA derivation and instruction ordering.
 *
 * @param mint0 - First mint address
 * @param mint1 - Second mint address
 * @returns Tuple of [token0, token1] in canonical order
 * @throws Error if mints are equal
 */
export function sortMints(mint0: Address, mint1: Address): [Address, Address] {
  const bytesA = addressCodec.encode(mint0);
  const bytesB = addressCodec.encode(mint1);

  for (let i = 0; i < 32; i++) {
    if (bytesA[i] < bytesB[i]) {
      return [mint0, mint1];
    }
    if (bytesA[i] > bytesB[i]) {
      return [mint1, mint0];
    }
  }

  throw new Error('Mints are equal - cannot create pool with identical tokens');
}

/**
 * Check if mints are in canonical order
 */
export function areMintsOrdered(mint0: Address, mint1: Address): boolean {
  const bytes0 = addressCodec.encode(mint0);
  const bytes1 = addressCodec.encode(mint1);

  for (let i = 0; i < 32; i++) {
    if (bytes0[i] < bytes1[i]) return true;
    if (bytes0[i] > bytes1[i]) return false;
  }
  return false; // equal
}

// ============================================================================
// PDA Derivation Functions
// ============================================================================

/**
 * Derive the AmmConfig PDA address
 * Seeds: ['config']
 */
export async function getConfigAddress(
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [textEncoder.encode(SEED_CONFIG)],
  });
}

/**
 * Derive the Pool PDA address for a token pair
 * Seeds: ['pool', token0_mint, token1_mint]
 *
 * Note: Mints will be automatically sorted if not in canonical order.
 */
export async function getPoolAddress(
  mint0: Address,
  mint1: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  const [token0, token1] = sortMints(mint0, mint1);
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [
      textEncoder.encode(SEED_POOL),
      addressCodec.encode(token0),
      addressCodec.encode(token1),
    ],
  });
}

/**
 * Derive the spot Pool PDA address for a token pair and immutable fee tier.
 * Seeds: ['spot_pool', token0_mint, token1_mint, swap_fee_bps_le]
 *
 * Note: Mints will be automatically sorted if not in canonical order.
 */
export async function getSpotPoolAddress(
  mint0: Address,
  mint1: Address,
  swapFeeBps: number,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  const [token0, token1] = sortMints(mint0, mint1);
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [
      textEncoder.encode(SEED_SPOT_POOL),
      addressCodec.encode(token0),
      addressCodec.encode(token1),
      u16Seed(swapFeeBps),
    ],
  });
}

/**
 * Derive the hooked spot Pool PDA for a token pair, fee tier, and immutable
 * swap-hook configuration.
 * Seeds: ['spot_pool', token0_mint, token1_mint, swap_fee_bps_le, hook_program, hook_flags_le]
 *
 * Note: Mints will be automatically sorted if not in canonical order.
 */
export async function getHookedSpotPoolAddress(
  mint0: Address,
  mint1: Address,
  swapFeeBps: number,
  hookProgram: Address,
  hookFlags: number,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  const [token0, token1] = sortMints(mint0, mint1);
  assertHookedSpotPoolConfig(hookProgram, hookFlags);

  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [
      textEncoder.encode(SEED_SPOT_POOL),
      addressCodec.encode(token0),
      addressCodec.encode(token1),
      u16Seed(swapFeeBps),
      addressCodec.encode(hookProgram),
      u32Seed(hookFlags),
    ],
  });
}

/**
 * Derive the Pool authority PDA (vault owner)
 * Seeds: ['authority', pool]
 */
export async function getPoolAuthorityAddress(
  pool: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [textEncoder.encode(SEED_AUTHORITY), addressCodec.encode(pool)],
  });
}

/**
 * Derive the token0 vault PDA.
 * Seeds: ['vault0', pool]
 */
export async function getPoolVault0Address(
  pool: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [textEncoder.encode(SEED_VAULT0), addressCodec.encode(pool)],
  });
}

/**
 * Derive the token1 vault PDA.
 * Seeds: ['vault1', pool]
 */
export async function getPoolVault1Address(
  pool: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [textEncoder.encode(SEED_VAULT1), addressCodec.encode(pool)],
  });
}

/**
 * Derive the Position PDA address
 * Seeds: ['position', pool, owner, position_id_le_bytes]
 */
export async function getPositionAddress(
  pool: Address,
  owner: Address,
  positionId: bigint,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  const positionIdBytes = new Uint8Array(8);
  const view = new DataView(positionIdBytes.buffer);
  view.setBigUint64(0, positionId, true); // little-endian

  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [
      textEncoder.encode(SEED_POSITION),
      addressCodec.encode(pool),
      addressCodec.encode(owner),
      positionIdBytes,
    ],
  });
}

/**
 * Derive the OracleState PDA address
 * Seeds: ['oracle', pool]
 */
export async function getOracleAddress(
  pool: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [textEncoder.encode(SEED_ORACLE), addressCodec.encode(pool)],
  });
}

/**
 * Derive the protocol fee owner PDA address
 * Seeds: ['protocol_fee_owner', pool]
 */
export async function getProtocolFeeOwnerAddress(
  pool: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  return getProgramDerivedAddress({
    programAddress: programId,
    seeds: [
      textEncoder.encode(SEED_PROTOCOL_FEE_OWNER),
      addressCodec.encode(pool),
    ],
  });
}

/**
 * Derive the protocol fee position PDA address.
 * Seeds: ['position', pool, protocol_fee_owner, 0]
 */
export async function getProtocolFeePositionAddress(
  pool: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<ProgramDerivedAddress> {
  const [protocolFeeOwner] = await getProtocolFeeOwnerAddress(pool, programId);
  return getPositionAddress(pool, protocolFeeOwner, 0n, programId);
}

// ============================================================================
// Batch PDA Derivation
// ============================================================================

/**
 * Derive all PDAs needed for pool initialization
 */
export async function getPoolInitAddresses(
  mint0: Address,
  mint1: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<{
  token0: Address;
  token1: Address;
  pool: ProgramDerivedAddress;
  authority: ProgramDerivedAddress;
  vault0: ProgramDerivedAddress;
  vault1: ProgramDerivedAddress;
  config: ProgramDerivedAddress;
  protocolFeeOwner: ProgramDerivedAddress;
  protocolFeePosition: ProgramDerivedAddress;
}> {
  const [token0, token1] = sortMints(mint0, mint1);
  const [config, pool] = await Promise.all([
    getConfigAddress(programId),
    getPoolAddress(token0, token1, programId),
  ]);
  const [authority, vault0, vault1, protocolFeeOwner] = await Promise.all([
    getPoolAuthorityAddress(pool[0], programId),
    getPoolVault0Address(pool[0], programId),
    getPoolVault1Address(pool[0], programId),
    getProtocolFeeOwnerAddress(pool[0], programId),
  ]);
  const protocolFeePosition = await getPositionAddress(
    pool[0],
    protocolFeeOwner[0],
    0n,
    programId,
  );

  return {
    token0,
    token1,
    pool,
    authority,
    vault0,
    vault1,
    config,
    protocolFeeOwner,
    protocolFeePosition,
  };
}

/**
 * Derive all PDAs needed for swap operation
 */
export async function getSwapAddresses(
  pool: Address,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<{
  config: ProgramDerivedAddress;
  authority: ProgramDerivedAddress;
  oracle: ProgramDerivedAddress;
}> {
  const [config, authority, oracle] = await Promise.all([
    getConfigAddress(programId),
    getPoolAuthorityAddress(pool, programId),
    getOracleAddress(pool, programId),
  ]);

  return { config, authority, oracle };
}

/**
 * Derive all PDAs needed for liquidity operations
 */
export async function getLiquidityAddresses(
  pool: Address,
  owner: Address,
  positionId: bigint,
  programId: Address = CPMM_PROGRAM_ID,
): Promise<{
  config: ProgramDerivedAddress;
  authority: ProgramDerivedAddress;
  position: ProgramDerivedAddress;
  protocolFeeOwner: ProgramDerivedAddress;
  protocolFeePosition: ProgramDerivedAddress;
  oracle: ProgramDerivedAddress;
}> {
  const [config, authority, position, protocolFeeOwner, oracle] =
    await Promise.all([
      getConfigAddress(programId),
      getPoolAuthorityAddress(pool, programId),
      getPositionAddress(pool, owner, positionId, programId),
      getProtocolFeeOwnerAddress(pool, programId),
      getOracleAddress(pool, programId),
    ]);
  const protocolFeePosition = await getPositionAddress(
    pool,
    protocolFeeOwner[0],
    0n,
    programId,
  );

  return {
    config,
    authority,
    position,
    protocolFeeOwner,
    protocolFeePosition,
    oracle,
  };
}
